import { InjectionToken } from '@angular/core';

/**
 * A fully-resolved toast render request (contracts/notification-conventions.md).
 * The facade computes every convention; the outlet only renders.
 */
export interface ToastRequest {
  readonly tier: 'info' | 'success' | 'warning' | 'error';
  readonly message: string;
  /** Present on error toasts when the failure envelope carried one. */
  readonly correlationId?: string;
  /** Auto-dismiss delay in ms; null = persistent until dismissed (error tier). */
  readonly autoDismissMs: number | null;
  /** ARIA semantics for the live region. */
  readonly role: 'status' | 'alert';
  readonly ariaLive: 'polite' | 'assertive';
  /** Error toasts carry an explicit, accessibly-named dismiss button. */
  readonly requiresDismissButton: boolean;
}

/**
 * Render endpoint for toast requests (T035 driver seam). The ONLY
 * implementation is the NgxJonasToastService adapter (D4; wired by T037 after
 * the integration spike) — screens never see this token, and tests substitute
 * a recording fake. `onSettled` MUST fire exactly once, on user dismissal or
 * auto-dismiss expiry, so the facade can free the visible slot (FIFO queue).
 */
export interface ToastOutlet {
  show(request: ToastRequest, onSettled: () => void): void;
}

export const TOAST_OUTLET = new InjectionToken<ToastOutlet>('TOAST_OUTLET');
